import { Logger } from '@hot-club-http/client/utils'
import type { QueryClient } from '@tanstack/react-query'

import HotClubHttp from './hotClubHttp'
import HotClubHttpGlobals from './hotClubHttpGlobals'
import type { CreateQueryClientParams, OptionsParams } from './types'

/**
 * HttpClient is a utility class that manages shared instances of the custom HTTP client
 * `HotClubHttp`. It integrates with React Query and Axios, providing a centralized approach
 * to create, retrieve, and manage HTTP clients across different parts of an application.
 *
 * It includes methods for:
 * - `createInstance`: Creates or retrieves shared HTTP client instances.
 * - `getInstance`: Retrieves an existing HTTP client instance by key.
 * - `createQueryClient`: Initializes a React Query `QueryClient` for managing server state.
 * - `updateAllHeaders`: Updates global headers across all HTTP client instances.
 * - `removeHeaders`: Removes specific headers from all HTTP client instances.
 * - `cancelAllRequests`: Cancels all ongoing HTTP requests globally.
 */
class HttpClient {
  /**
   * Creates and returns a new `HotClubHttp` instance or retrieves an existing one with the same key.
   * This method ensures that only one shared instance exists for a given unique key.
   *
   * @param {string} key - **(Required)** - A unique identifier for the HTTP client instance.
   * @param {string} baseURL - **(Required)** - The base URL to be used for all HTTP requests by this instance.
   * @param {OptionsParams} [options] - Optional parameters for configuring the instance,
   *                                    such as custom headers, cache settings, or debug options.
   * @returns {HotClubHttp} The shared `HotClubHttp` instance associated with the given key.
   *
   * @throws {Error} Throws an error if `key` or `baseURL` is missing or invalid.
   *
   * @example
   * // Creating or retrieving a shared HTTP client instance
   * const apiInstance = HttpClient.createInstance('user-api', 'https://api.example.com', {
   *   headers: { Authorization: 'Bearer token' },
   *   debug: true
   * });
   */
  static createInstance(key: string, baseURL: string, options?: OptionsParams): HotClubHttp {
    try {
      if (!key || !baseURL)
        throw new Error(
          `[HotClubHttp][createInstance] Missing parameters, key:${key}, baseUrl:${baseURL}`
        )
      const instance = HotClubHttp.getInstance(key, baseURL, options)

      instance && options?.debug && Logger.info('createInstance', { instance })

      return instance
    } catch (e) {
      Logger.error('createInstance', { error: e?.message })
      throw e
    }
  }

  /**
   * Creates a new React Query `QueryClient` using the default `HotClubHttp` instance.
   * This `QueryClient` can be used to manage server state, caching, and query execution.
   *
   * @returns {QueryClient} A React Query `QueryClient` instance ready for use.
   *
   * @throws {Error} Throws an error if the default `HotClubHttp` instance cannot be initialized.
   *
   * @example
   * // Create and use a QueryClient for React Query
   * const queryClient = HttpClient.createQueryClient();
   */
  static createQueryClient(params?: CreateQueryClientParams): QueryClient {
    try {
      const { debugMode } = params || { debugMode: false }
      const instance = HotClubHttpGlobals.initQueryClient({ debugMode })

      return instance
    } catch (e) {
      Logger.error('createQueryClient', { error: e?.message })
      throw e
    }
  }

  /**
   * Retrieves an existing `HotClubHttp` instance by its unique key.
   * If no instance exists for the provided key, this method returns `null`.
   *
   * @param {string} key - **(Required)** - The unique key identifying the desired HTTP client instance.
   * @returns {HotClubHttp | null} The corresponding `HotClubHttp` instance or `null` if not found.
   *
   * @throws {Error} Throws an error if the `key` is missing or invalid.
   *
   * @example
   * // Retrieve an existing instance
   * const userApiInstance = HttpClient.getInstance('user-api');
   * if (userApiInstance) {
   *   userApiInstance.get('/profile');
   * }
   */
  static getInstance(key: string): HotClubHttp | null {
    try {
      if (!key) throw new Error(`[HotClubHttp][getInstance] Missing parameters, ${key}`)

      return HotClubHttpGlobals.getInstanceByKey(key)
    } catch (e) {
      Logger.error('getInstance', { error: e?.message })
      throw e
    }
  }

  /**
   * Updates global headers for all active `HotClubHttp` instances.
   * This method is useful for dynamically updating headers, such as authorization tokens,
   * without having to reinitialize HTTP clients.
   *
   * @param {Record<string, string>} headers - **(Required)** - A key-value map containing header keys and values to update.
   *
   * @example
   * // Update the Authorization header globally
   * HttpClient.updateAllHeaders({ Authorization: 'Bearer new-token' });
   */
  static updateAllHeaders(headers: Record<string, string>): void {
    try {
      HotClubHttpGlobals.updateAllInstancesHeaders(headers)
    } catch (e) {
      Logger.error('updateAllHeaders', { error: e?.message })
      throw e
    }
  }

  /**
   * Cancels all ongoing HTTP requests across all active `HotClubHttp` instances.
   * This is particularly useful in scenarios like user logout or global cleanup processes.
   *
   * @example
   * // Cancel all ongoing requests
   * HttpClient.cancelAllRequests();
   */
  static cancelAllRequests() {
    try {
      HotClubHttpGlobals.cancelAllRequests()
    } catch (e) {
      Logger.error('cancelAllRequests', { error: e?.message })
      throw e
    }
  }

  /**
   * Removes one or multiple headers from the global configuration of all `HotClubHttp` instances.
   * Useful for clearing sensitive headers like authorization tokens when they are no longer needed.
   *
   * @param {string | string[]} keys - **(Required)** - A string or an array of strings representing the header keys to be removed.
   *
   * @example
   * // Remove a single header
   * HttpClient.removeHeaders('Authorization');
   *
   * // Remove multiple headers
   * HttpClient.removeHeaders(['Authorization', 'X-Custom-Header']);
   */
  static removeHeaders(keys: string | string[]): void {
    try {
      HotClubHttpGlobals.removeInstanceHeader(keys)
    } catch (e) {
      Logger.error('removeHeaders', { error: e?.message })
      throw e
    }
  }
}

export default HttpClient
